import * as Yup from 'yup';

/**
 * Enumeration of possible validation error types
 */
export enum ErrorKey {
  DescriptionMissing,
  POIdMissing,
  ReasonForChangeMissing,
  CurrencyMissing,
  OtherReasonMissing,
  FBNsMissing,
  WorkPerformedMissing,
  OSDImpactMissing,
  EDDMissing,
  DescriptionTooLong,
  GCMMissing,
  ChangeOrderTypeMissing,
  BusinessTypeMissing,
  APIError
}

/**
 * Mapping of error keys to their display messages
 */
const ErrorMessages = {
  [ErrorKey.DescriptionMissing]: 'Change Order description is required.',
  [ErrorKey.POIdMissing]: 'Purchase Order ID is required.',
  [ErrorKey.ReasonForChangeMissing]: 'Reason for change is required.',
  [ErrorKey.CurrencyMissing]: 'Currency is required.',
  [ErrorKey.OtherReasonMissing]: 'Other reason description is required when "Other" is selected.',
  [ErrorKey.FBNsMissing]: 'FBN is required',
  [ErrorKey.WorkPerformedMissing]: 'Work performed status is required.',
  [ErrorKey.OSDImpactMissing]: 'OSD Impact status is required.',
  [ErrorKey.EDDMissing]: 'Estimated Delivery Date is required.',
  [ErrorKey.DescriptionTooLong]: 'Description must not exceed 500 words.',
  [ErrorKey.GCMMissing]: 'GCM is required.',
  [ErrorKey.ChangeOrderTypeMissing]: 'Change Order type is required.',
  [ErrorKey.BusinessTypeMissing]: 'Business type is required.',
  [ErrorKey.APIError]: 'Failed to create change order. Please try again.'
} as const;

/**
 * Retrieves the display message for a given error key
 * @param key - The error key to get the message for
 * @returns The corresponding error message
 */
export function getErrorMessage(key: ErrorKey): string {
  return ErrorMessages[key];
}

/**
 * Validation schema for general information section
 * Defines rules for all required fields and their interdependencies
 */
const generalInformationSchema = Yup.object().shape({
  description: Yup.string()
    .required(ErrorKey.DescriptionMissing.toString())
    .test(
      'word-count', 
      ErrorKey.DescriptionTooLong.toString(), 
      value => {
        const wordCount = value?.trim().split(/\s+/).length || 0;
        return wordCount <= 500;
      }
    ),
  fbns: Yup.array()
  .test(
    'non-empty-fbn', 
    ErrorKey.FBNsMissing.toString(), 
    (arr, context) => {
      if (context.parent.changeOrderType === 'PAC_NON_RFQ') return true;
      return arr!.length > 0 && arr!.every(fbn => fbn.trim().length > 0);
    }
  ),
  // PO IDs validation
  poIds: Yup.array()
    .of(Yup.string().required())
    .test(
      'non-empty-po', 
      ErrorKey.POIdMissing.toString(), 
      arr => arr!.length > 0 && arr!.every(po => po.trim().length > 0)
    ),

  reasonForChange: Yup.string()
    .required(ErrorKey.ReasonForChangeMissing.toString()),

  // Other reason validation - required only when reason is 'other'
  otherReasonForChange: Yup.string()
    .when('reasonForChange', {
      is: (value: string) => value === 'other',
      then: (schema) => schema.required(ErrorKey.OtherReasonMissing.toString())
    }),

  workPerformed: Yup.boolean()
    .required(ErrorKey.WorkPerformedMissing.toString()),

  osdImpact: Yup.boolean()
    .required(ErrorKey.OSDImpactMissing.toString()),

  // EDD validation - required only when OSD impact is true
  expectedDeliveryDate: Yup.string()
    .when('osdImpact', {
      is: (value: boolean) => value,
      then: (schema) => schema.required(ErrorKey.EDDMissing.toString())
    }),

  // GCM validation - required field
  gcm: Yup.string()
    .required(ErrorKey.GCMMissing.toString())
});

/**
 * Validates the general information section of the Change Order wizard
 * Returns error keys instead of messages because different UI components
 * may display the errors differently
 * 
 * @param generalInformation - The general information data to validate
 * @param changeOrderType - The type of change order being created
 * @returns Array of error keys if validation fails, empty array if validation passes
 */
export function validateGeneralInformation(
  generalInformation: any, 
  changeOrderType: string
): ErrorKey[] {

  try {
    generalInformationSchema.validateSync(
      { ...generalInformation, changeOrderType }, 
      { abortEarly: false }
    );
    return [];
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      const errors = error.inner.map(err => {
        // Convert the string back to ErrorKey enum value
        const errorKey = parseInt(err.message) as ErrorKey;
        return errorKey;
      });
      return errors;
    }
    return [];
  }
}
